/*
 * Copyright (c) 2022 OTB Africa. All rights reserved. <https://otbafrica.com>
 *
 * Created Date: Tuesday, September 6th 2022, 2:19:03 pm
 * Author: OTB Africa, developers@otbafrica.com
 *
 * Use of this source code is governed by the license that can be found in
 * the LICENSE file or at https://developers.otbafrica.com/licenses/
 */

import React from "react";
import List from "@mui/material/List";
import Paper from "@mui/material/Paper";
import Skeleton from "@mui/material/Skeleton";
import { DnDItemTypes } from "config";
import { TaskTemplate } from "lib/schemas";
import { useWorkflowContext } from "service-builder/providers";
import { AnimatedDragDropListItemWrapper, ListItemDragLayer } from "ui-widgets/drag-and-drop";
import { TaskTemplateListItemDefaultWidget, TaskTemplateListItemDefaultWidgetLayout } from "./TaskTemplateListItem";

interface WorkflowAsListProps {
  handleClickTaskTemplate: (selectedTaskTemplate: TaskTemplate) => void;
}

export const WorkflowAsList = ({ handleClickTaskTemplate }: WorkflowAsListProps) => {
  const {
    taskTemplateListContext: {
      itemsListState,
      filteredItems,
      startOfListPosition,
      handleDragStart,
      handleDragEnter,
      handleDrop,
      findItem,
      updateList,
    },
  } = useWorkflowContext();

  const { selectedItemId } = itemsListState;

  const isDraggable = (taskTemplate: TaskTemplate) => !(taskTemplate.type === "StartFunction");

  return (
    <List
      component="nav"
      aria-label="task-template-list"
      className="os-task-template-list-view-container"
      onTouchStart={handleDragStart}
    >
      <TaskTemplateDragLayer findItem={findItem} />
      {filteredItems.map((taskTemplate, index) => (
        <AnimatedDragDropListItemWrapper
          key={taskTemplate.id}
          currentItemId={taskTemplate.id}
          currentItemPosition={taskTemplate.sequence}
          firstItemPosition={startOfListPosition}
          handleDragEnter={handleDragEnter}
          handleDrop={handleDrop}
          itemType={DnDItemTypes.OS_TASK_TEMPLATE_ITEM}
          isDraggableItem={isDraggable(taskTemplate)}
        >
          <TaskTemplateListItemDefaultWidget
            taskTemplate={taskTemplate}
            selected={selectedItemId === taskTemplate.id}
            onClick={(event: React.MouseEvent<HTMLDivElement, MouseEvent>) => handleClickTaskTemplate(taskTemplate)}
            handleListItemEdit={updateList}
          />
        </AnimatedDragDropListItemWrapper>
      ))}

      {itemsListState.itemCreationQueue.map((newitem, index) => (
        <NewTaskTemplateCreationLoader
          key={`new-${index}`}
          repositioningFactor={newitem.sequence - startOfListPosition}
        />
      ))}
    </List>
  );
};

const NewTaskTemplateCreationLoader = ({ repositioningFactor }: { repositioningFactor: number }) => {
  return (
    <div
      style={{
        position: "absolute",
        width: "100%",
        transform: `translateY(${repositioningFactor * 100}%)`,
        paddingTop: "4px",
      }}
    >
      <Paper>
        <Skeleton variant="rectangular" height={87} />
      </Paper>
    </div>
  );
};

const TaskTemplateDragLayer = React.memo(({ findItem }: any) => {
  return (
    <ListItemDragLayer
      itemType={DnDItemTypes.OS_TASK_TEMPLATE_ITEM}
      findItem={findItem}
      PreviewComponent={TaskTemplateDragLayerPreview}
    />
  );
});

const TaskTemplateDragLayerPreview = ({ item: taskTemplate }: { item: TaskTemplate }) => (
  <Paper className="os-task-template-drag-preview">
    <TaskTemplateListItemDefaultWidgetLayout
      type={taskTemplate.type}
      primary={taskTemplate.title}
      selected={true}
      handleOnClick={null}
    />
  </Paper>
);
